/* eslint-disable no-await-in-loop */
import { PITContractMethod, TokenMethod } from "context/tokens/methodName";
import { BigNumber, Contract } from "ethers";
import BNjs from "bignumber.js";
import { formatUnits } from "utils/number";
import { useFetchGraphData } from "hooks/query/graphQL/useFetchGraphData";
import { useMultiCallContractInstance } from "hooks/contract/multicall/useMultiCallContract";
import useWallet from "hooks/useWallet";
import { flatten, get, groupBy, isEmpty } from "lodash";
import { useQuery } from "react-query";
import { ERC20TokenABI } from "utils/ethereum/abi";
import {
  getPriceProviderAggregatorContract,
  getPrimaryIndexToken,
  getPythProvider,
} from "utils/ethereum/contracts";
import { getDataToUpdatePrice } from "utils/ethereum/getDataToUpdatePrice";
import {
  decodeResultMulticallZkSync,
  formatMulticallResults,
} from "utils/contract/decodeResultMulticall";
import handleGetListTokenProvider from "utils/ethereum/handleGetListTokenProvider";
import { REACT_APP_ACCOUNT_HAVING_ETH } from "constants/NetworkChainId";

const GET_PRICE_ZKSYNC_METHOD = "getUpdatedPrice";

const handleGetDataFromResultZkSyncMulticall = ({ contractRefs = [], methodName, resultInRaw }) =>
  flatten(
    contractRefs.map((ref) => {
      const contract = get(resultInRaw, [ref], []);

      const groupByMethod = groupBy(contract, "methodName");
      const methodResult = get(groupByMethod, methodName, []);

      return [...methodResult].map((e) => ({ ...e, contractAddress: e.contract.address }));
    })
  );

const handleGetRequest = async ({
  PITToken,
  availableBorrowTokens,
  account,
  provider,
  PriceContractInfo,
  priceProviderList,
  pythAddress,
}) => {
  const PITTokenContract = new Contract(PITToken.address, PITToken.abi, provider);
  const requests = [];
  const fTokenRequest = [];
  const listRequestPrice = [];
  const PriceContract = new Contract(PriceContractInfo.address, PriceContractInfo.abi, provider);

  for (let idx = 0; idx < availableBorrowTokens.length; idx += 1) {
    const token = availableBorrowTokens[idx];
    const TokenContract = new Contract(token.address, ERC20TokenABI, provider);

    fTokenRequest.push({
      target: PITToken.address,
      callData: PITTokenContract.interface.encodeFunctionData(
        PITContractMethod.lendingTokenInfoMethod,
        [token.address]
      ),
      reference: token.address,
      methodName: PITContractMethod.lendingTokenInfoMethod,
      methodParameters: [token.address],
      contract: PITTokenContract,
      contractName: "PITContract",
      value: BigNumber.from(0),
    });

    const listTokenRequest = [
      {
        target: token.address,
        callData: TokenContract.interface.encodeFunctionData(TokenMethod.balanceOf, [account]),
        reference: token.address,
        methodName: TokenMethod.balanceOf,
        methodParameters: [account],
        contract: TokenContract,
        value: BigNumber.from(0),
        contractName: token.address,
      },
      {
        target: token.address,
        callData: TokenContract.interface.encodeFunctionData(TokenMethod.decimals),
        reference: token.address,
        methodName: TokenMethod.decimals,
        methodParameters: [],
        contract: TokenContract,
        value: BigNumber.from(0),
        contractName: token.address,
      },
      {
        target: token.address,
        callData: TokenContract.interface.encodeFunctionData(TokenMethod.name),
        reference: token.address,
        methodName: TokenMethod.name,
        methodParameters: [],
        contract: TokenContract,
        value: BigNumber.from(0),
        contractName: token.address,
      },
      {
        target: token.address,
        callData: TokenContract.interface.encodeFunctionData(TokenMethod.symbol),
        reference: token.address,
        methodName: TokenMethod.symbol,
        methodParameters: [],
        contract: TokenContract,
        contractName: token.address,
        value: BigNumber.from(0),
      },
    ];

    requests.push(...listTokenRequest);

    const tokenPriceProvider = priceProviderList[token.address];

    const isUsingPyth = pythAddress?.toString() === tokenPriceProvider.toString();

    let payableAmount = BigNumber.from(1);
    let updateData = [];

    if (isUsingPyth) {
      const dataUpdatePrice = await getDataToUpdatePrice([token.address], PriceContract);
      payableAmount = dataUpdatePrice.payableAmount;
      updateData = dataUpdatePrice.updateData;
    }

    listRequestPrice.push({
      target: PriceContract.address,
      callData: PriceContract.interface.encodeFunctionData(GET_PRICE_ZKSYNC_METHOD, [
        token.address,
        updateData,
      ]),
      reference: token.address,
      methodName: GET_PRICE_ZKSYNC_METHOD,
      methodParameters: [token.address, updateData],
      value: payableAmount,
      contract: PriceContract,
      contractName: "PriceProviderContract",
    });
  }

  return [...fTokenRequest, ...requests, ...listRequestPrice];
};

export const useUserTokenInfo = () => {
  const { availableBorrowTokens } = useFetchGraphData();

  const { account, chainId, provider } = useWallet();
  const multicallContract = useMultiCallContractInstance();

  const PriceContractInfo = getPriceProviderAggregatorContract(chainId);

  const pythAddress = getPythProvider(chainId).address;

  const res = useQuery(
    ["f-token-info", account, chainId],
    async () => {
      const PITToken = getPrimaryIndexToken(chainId);

      const priceProviderList = await handleGetListTokenProvider(
        availableBorrowTokens,
        PriceContractInfo,
        provider,
        multicallContract,
        chainId
      );

      const lendTokenRequest = await handleGetRequest({
        PITToken,
        availableBorrowTokens,
        account,
        provider,
        PriceContractInfo,
        priceProviderList,
        pythAddress,
      });
      let results;

      try {
        const valueOfRequest = lendTokenRequest.map((r) =>
          "value" in r ? r.value : BigNumber.from(0)
        );
        const totalValue = valueOfRequest.reduce((pre, cur) => pre.add(cur), BigNumber.from(0));

        const resultMulticall = await multicallContract.callStatic.aggregate3Value(
          lendTokenRequest,
          {
            value: totalValue,
            from: REACT_APP_ACCOUNT_HAVING_ETH[chainId],
          }
        );
        const returnData = formatMulticallResults(resultMulticall);

        results = decodeResultMulticallZkSync(lendTokenRequest, returnData);
      } catch (error) {
        console.warn("useUserTokenInfo error: ", error);
      }

      const priceOfTokens = handleGetDataFromResultZkSyncMulticall({
        contractRefs: ["PriceProviderContract"],
        methodName: GET_PRICE_ZKSYNC_METHOD,
        resultInRaw: results,
      }).map((o) => {
        const priceBN = get(o, ["returnValues", 0], 0);
        const priceDecimal = get(o, ["returnValues", 1], 0);
        const lendingToken = get(o, ["reference"], "");
        return {
          price: formatUnits(priceBN, priceDecimal),
          lendingToken,
        };
      });

      const fTokens = handleGetDataFromResultZkSyncMulticall({
        contractRefs: ["PITContract"],
        methodName: PITContractMethod.lendingTokenInfoMethod,
        resultInRaw: results,
      }).map((o) => ({
        lendingToken: get(o, ["methodParameters", 0], ""),
        ftoken: get(o, ["returnValues", 2], ""),
      }));

      const decimalOfLendingToken = handleGetDataFromResultZkSyncMulticall({
        contractRefs: availableBorrowTokens.map((l) => l.address),
        methodName: TokenMethod.decimals,
        resultInRaw: results,
      }).map((o) => ({
        token: get(o, "contractAddress", ""),
        value: BigNumber.from(get(o, ["returnValues"], "")).toString(),
      }));

      const balanceOfLendingToken = handleGetDataFromResultZkSyncMulticall({
        contractRefs: availableBorrowTokens.map((l) => l.address),
        methodName: TokenMethod.balanceOf,
        resultInRaw: results,
      }).map((o, idx) => {
        const lendingToken = get(o, "contractAddress", "");

        const balance = formatUnits(
          get(o, ["returnValues", 0], ""),
          decimalOfLendingToken[idx].value
        );
        const priceInfo = priceOfTokens.find((p) => p.lendingToken === lendingToken);

        return {
          token: lendingToken,
          balance,
          balanceInUsd: new BNjs(balance).multipliedBy(priceInfo.price).toString(),
          decimal: decimalOfLendingToken[idx].value,
          priceInfo,
          ...availableBorrowTokens.find((x) => x.address === lendingToken),
        };
      });

      return {
        fTokens,
        balanceOfLendingToken,
        priceLendingToken: priceOfTokens,
      };
    },
    {
      // initialData: {
      //   fTokens: [],
      //   balanceOfLendingToken: [],
      //   priceLendingToken: [],
      // },
      // enabled: false,
      enabled: !isEmpty(availableBorrowTokens), // && Boolean(account),
    }
  );

  // useEffect(() => {
  //   if (availableBorrowTokens.length) res.refetch();
  // }, [availableBorrowTokens.length, res]);

  return res;
};
